import React, { useEffect, useMemo, useState } from "react";
import { Key, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const MODES = [["auto", "Auto"], ["on", "Always On"], ["off", "Always Off"]];
const DAYS = [[7, "Last 7 days"], [15, "Last 15 days"], [30, "Last 30 days"], [60, "Last 60 days"], [90, "Last 90 days"], [180, "Last 6 months"], [365, "Last 1 year"], [0, "All time"]];
const fmt = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
const ago = (d) => (d === 0 ? "today" : d === 1 ? "1 day ago" : `${d} days ago`);

function ModeSwitch({ u, busy, onPick }) {
  return (
    <div className="inline-flex rounded-md border border-[#DCDCD3] overflow-hidden" data-testid={`access-mode-${u.id}`}>
      {MODES.map(([k, label]) => (
        <button key={k} disabled={busy} onClick={() => k !== u.mode && onPick(k)} data-testid={`access-mode-${u.id}-${k}`}
          className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider transition-colors disabled:opacity-60 ${u.mode === k ? "bg-[#385C4B] text-white" : "bg-white text-[#6B6B61] hover:bg-[#F0F0EA]"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

function AccessBadge({ u }) {
  const why = u.mode === "off" ? "off" : u.last_work ? "inactive" : "no work";
  return (
    <span data-testid={`access-status-${u.id}`}
      className={`inline-block px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.12em] rounded ${u.enabled ? "bg-[#385C4B] text-white" : "bg-[#F5F5F0] text-[#C8553D] border border-[#E8D5CF]"}`}>
      {u.enabled ? "Enabled" : `Disabled (${why})`}
    </span>
  );
}

function AccessRow({ u, busy, onMode, onDays }) {
  return (
    <tr className="border-t border-[#F0F0EA] hover:bg-[#F8F8F3]" data-testid={`access-row-${u.id}`}>
      <td className="px-3 py-2">
        <div className="font-medium">{u.display_name}</div>
        <div className="text-[10px] font-mono text-[#6B6B61]">@{u.username}</div>
      </td>
      <td className="px-3 py-2 text-xs whitespace-nowrap" data-testid={`access-last-work-${u.id}`}>
        {u.last_work ? <>{fmt(u.last_work)} <span className={u.idle_days > u.limit ? "text-[#C8553D]" : "text-[#6B6B61]"}>· {ago(u.idle_days)}</span></> : <span className="text-[#6B6B61]">No bookings yet</span>}
      </td>
      <td className="px-3 py-2"><ModeSwitch u={u} busy={busy} onPick={onMode} /></td>
      <td className="px-3 py-2 text-center"><AccessBadge u={u} /></td>
      <td className="px-3 py-2 text-right">
        <select value={u.ledger_visible_days} disabled={busy} onChange={(e) => onDays(Number(e.target.value))} data-testid={`access-days-${u.id}`}
          className="border border-[#DCDCD3] rounded px-2 py-1 text-xs bg-white disabled:opacity-50 focus:outline-none focus:border-[#385C4B]">
          {DAYS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </select>
      </td>
    </tr>
  );
}

export function LedgerAccessTab() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [savingId, setSavingId] = useState("");
  const load = () => api.get("/admin/ledger-access").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  const run = async (id, fn, msg) => {
    setSavingId(id);
    try { await fn(); await load(); if (msg) toast.success(msg); } catch (e) { toast.error(formatApiError(e)); } finally { setSavingId(""); }
  };
  const users = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = data?.users || [];
    return q ? list.filter((u) => `${u.display_name} ${u.username}`.toLowerCase().includes(q)) : list;
  }, [data, search]);
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#385C4B]" /></div>;
  const on = data.users.filter((u) => u.enabled).length;
  return (
    <section className="bg-white border border-[#EDEDE5] rounded-lg overflow-hidden" data-testid="ledger-access-tab">
      <header className="flex items-center gap-2 px-4 py-3 border-b border-[#F0F0EA] bg-[#F8F8F3]">
        <Key className="w-4 h-4 text-[#385C4B]" />
        <h2 className="font-semibold text-[#1C1C19] text-sm uppercase tracking-wider" style={{ fontFamily: "Outfit, sans-serif" }}>Ledger Access · Per Agency</h2>
      </header>
      <div className="p-4 space-y-3">
        <p className="text-sm text-[#6B6B61] leading-relaxed">
          <b>Auto</b>: the agency&apos;s ledger opens by itself while it has bookings, and closes after the days below without any new booking. It reopens as soon as a new booking arrives. <b>Always On / Always Off</b> override this.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs flex items-center gap-2" data-testid="ledger-access-limit">
            Auto-disable after
            <select value={data.inactive_days} disabled={savingId === "limit"} data-testid="ledger-access-limit-select"
              onChange={(e) => run("limit", () => api.put("/admin/ledger-access/settings", { inactive_days: Number(e.target.value) }), "Setting saved")}
              className="border border-[#DCDCD3] rounded px-2 py-1 text-xs bg-white focus:outline-none focus:border-[#385C4B]">
              {data.day_choices.map((d) => <option key={d} value={d}>{d} days</option>)}
            </select>
            without work
          </label>
          <span className="text-[10px] font-mono text-[#6B6B61] tracking-wider ml-2" data-testid="ledger-access-count">{on} enabled · {data.users.length - on} disabled</span>
          <button onClick={() => window.confirm("Set every agency to Auto?") && run("all", () => api.post("/admin/ledger-access/all-auto"), "All agencies set to Auto")}
            disabled={savingId === "all"} data-testid="ledger-access-all-auto"
            className="ml-auto px-3 py-2 bg-[#385C4B] hover:bg-[#385C4B]/90 text-white text-xs font-mono uppercase tracking-[0.1em] rounded-md disabled:opacity-60">
            Set All to Auto
          </button>
        </div>
        <div className="max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B61]" />
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or username…" data-testid="wallet-access-search"
            className="w-full bg-white border border-[#DCDCD3] focus:border-[#385C4B] focus:outline-none focus:ring-2 focus:ring-[#385C4B]/20 rounded-md pl-9 pr-3 py-2 text-sm" />
        </div>
        <div className="border border-[#DCDCD3] rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F0] text-[10px] uppercase tracking-wider text-[#6B6B61]">
              <tr>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-left px-3 py-2">Last Work</th>
                <th className="text-left px-3 py-2">Mode</th>
                <th className="text-center px-3 py-2">Access</th>
                <th className="text-right px-3 py-2">Visible Days</th>
              </tr>
            </thead>
            <tbody>
              {!users.length && <tr><td colSpan={5} className="text-center py-6 text-[#6B6B61]">No matching users.</td></tr>}
              {users.map((u) => (
                <AccessRow key={u.id} u={u} busy={savingId === u.id}
                  onMode={(mode) => run(u.id, () => api.patch(`/admin/ledger-access/${u.id}`, { mode }))}
                  onDays={(v) => run(u.id, () => api.patch(`/admin/users/${u.id}`, { ledger_visible_days: v }))} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
